import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { errorToastMessage } from "../../utils/toast";
import http from "../../utils/http";
import { Box, CircularProgress } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import Error404 from "../Layout/404";
import ScreeningAttempt from "./ScreeningAttempt";
import { VisitPhaseRoot } from "../../types/VisitPhases.types";
import { PhasesList } from "../Responses/types";
import { DateTime } from "luxon";
import { handlePhaseStatus } from "../Responses/utils";
import { getFormData } from "../../Redux/actions/responseAction";
import {
  setIsParticipant,
  setParticipantLang,
  setPhaseList,
  setResponseIsEditable,
  setResponseLoader,
  setScreeningAttemptId,
} from "../../Redux/reducers/responseSlice";
import { useQuery } from "../../utils/hooks";

const Screening = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const { id: studyId } = useParams();
  const [error, setError] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const backgroundColor = useAppSelector(
    (state) => state.study.backgroundColor
  );
  const [languages, setLanguages] = useState<any[]>([]);
  const [toggle, setToggle] = useState(false);

  const studySlug = useQuery().get("studySlug") || "";

  useEffect(() => {
    async function getSurveyData() {
      try {
        dispatch(setResponseLoader(true));
        const langRes = await http.get(
          `/data/study-languages?studySlug=${studySlug}`
        );

        const studyLangs = langRes?.data?.data || [];

        let lang = localStorage.getItem("lang") || "en";

        if (lang !== "en") {
          dispatch(setParticipantLang(lang));
        }
        setLanguages(studyLangs);

        const attemptRes = await http.post(
          `/study/${studyId}/participant-screening-responses/attempt`
        );

        dispatch(
          setScreeningAttemptId({
            screeningAttemptId: attemptRes.data?.data?.attemptId,
          })
        );

        let { data } = await http.get<VisitPhaseRoot>(
          `/participant/study/${studyId}/study-phase/participant/screening`
        );

        if (!data?.data?.[0]?.id) {
          setError(true);
          dispatch(setResponseLoader(false));
          return;
        } else {
          dispatch(setIsParticipant(true));
          dispatch(setResponseIsEditable(!attemptRes.data?.data?.isReadOnly));
          const phases: PhasesList[] = data?.data
            .map((phase) => {
              const phaseForms = phase.phaseForms
                .map((phaseForm) => ({
                  position: phaseForm.position,
                  id: phaseForm.form.id,
                  name: phaseForm.form.name,
                  attempt: phaseForm.form.formAttempts?.[0]?.id,
                  status: phaseForm.form.formAttempts?.[0]?.status || "active",
                  locked: phaseForm.form.formAttempts?.[0]?.isLocked || false,
                  lockedBy: phaseForm.form.formAttempts?.[0]?.lockedBy
                    ? phaseForm.form.formAttempts?.[0]?.lockedBy?.firstName +
                      " " +
                      phaseForm.form.formAttempts?.[0]?.lockedBy?.lastName
                    : "",
                  lockedAt: phaseForm.form.formAttempts?.[0]?.lockedAt
                    ? DateTime.fromISO(
                        phaseForm.form.formAttempts?.[0]?.lockedAt
                      ).toFormat("dd MMMM yyyy hh:mm a")
                    : "",
                  isSigned: phaseForm.form.formAttempts?.[0]?.isSigned || false,
                  signedBy: phaseForm.form.formAttempts?.[0]?.signedBy
                    ? phaseForm.form.formAttempts?.[0]?.signedBy?.firstName +
                      " " +
                      phaseForm.form.formAttempts?.[0]?.signedBy?.lastName
                    : "",
                  signedAt: phaseForm.form.formAttempts?.[0]?.signedAt
                    ? DateTime.fromISO(
                        phaseForm.form.formAttempts?.[0]?.signedAt
                      ).toFormat("dd MMMM yyyy hh:mm a")
                    : "",
                  phaseName: phase.name,
                  phaseId: phase.id,
                }))
                .sort((a, b) => a.position - b.position);

              const phaseStatus = handlePhaseStatus(phaseForms);

              return {
                id: phase.id,
                name: phase.name,
                position: phase.position,
                phaseStatus: phaseStatus,
                phaseForms: phaseForms,
              };
            })
            .sort((a, b) => a.position - b.position);
          let selectedForm = null;
          if (phases.length > 0) {
            const formId = sessionStorage.getItem("response-visit-form");

            if (formId) {
              phases.forEach((phase) => {
                const selForm = phase.phaseForms.find((pf) => pf.id === formId);
                if (selForm) {
                  selectedForm = selForm;
                }
              });
            }
            if (selectedForm) {
              dispatch(getFormData({ studyId, selectedForm }));
            } else {
              phases.some((phase) => {
                const selForm = phase.phaseForms.find(
                  (pf) => pf.status === "active" || pf.status === "inprogress"
                );
                if (selForm) {
                  selectedForm = selForm;
                }
                return !!selForm;
              });
              if (!selectedForm) {
                selectedForm = phases?.[0]?.phaseForms?.[0];
              }
              if (selectedForm) {
                dispatch(getFormData({ studyId, selectedForm }));
              } else {
                dispatch(setResponseLoader(false));
              }
            }
          }
          dispatch(setPhaseList({ list: phases }));
        }
      } catch (err: any) {
        errorToastMessage(err as Error);
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    if (studyId) {
      getSurveyData();
    }
  }, [studyId, dispatch, studySlug, toggle]);

  const refreshPage = () => {
    setToggle((prev: boolean) => !prev);
  };

  return (
    <Box sx={{ backgroundColor: backgroundColor }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Error404 />
      ) : (
        <ScreeningAttempt languages={languages} refreshPage={refreshPage} />
      )}
    </Box>
  );
};

export default Screening;
